import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

import { getApiBaseUrl } from '@core/api-url';
import {
  AccessUser,
  AccessSite,
  CreateMenuItemRequest,
  CreateRoleRequest,
  Feature,
  GoSkalerSettingUpsert,
  Me,
  MenuItem,
  Permission,
  Role,
  TenantFeature,
  TenantFeatureToggle,
  UpdateMenuItemRequest,
  UpdateRoleRequest
} from '@core/models/access.model';

@Injectable({ providedIn: 'root' })
export class AccessApiService {
  private readonly http = inject(HttpClient);
  private readonly base = getApiBaseUrl();
  private readonly me = `${this.base}/api/me`;
  private readonly platform = `${this.base}/api/platform`;

  // ── Identity ──
  getMe(): Observable<Me> {
    return this.http.get<Me>(this.me);
  }

  getMeForTenant(tenantKey: string): Observable<Me> {
    return this.http.get<Me>(this.me, { headers: new HttpHeaders({ 'X-Tenant-Key': tenantKey }) });
  }

  upsertGoskalerSettings(tenantKey: string, settings: GoSkalerSettingUpsert[]): Observable<void> {
    return this.http.put<void>(`${this.me}/goskaler-settings`, { settings }, {
      headers: new HttpHeaders({ 'X-Tenant-Key': tenantKey })
    });
  }

  // ── Catalog ──
  getFeatures(): Observable<Feature[]> {
    return this.http.get<Feature[]>(`${this.platform}/features`);
  }

  getPermissions(): Observable<Permission[]> {
    return this.http.get<Permission[]>(`${this.platform}/permissions`);
  }

  // ── Tenant provisioning ──
  getTenantFeatures(clientId: string): Observable<TenantFeature[]> {
    return this.http.get<TenantFeature[]>(`${this.platform}/tenants/${clientId}/features`);
  }

  setTenantFeatures(clientId: string, features: TenantFeatureToggle[]): Observable<TenantFeature[]> {
    return this.http.put<TenantFeature[]>(`${this.platform}/tenants/${clientId}/features`, { features });
  }

  // ── Roles ──
  getRoles(clientId?: string | null): Observable<Role[]> {
    const params = clientId ? { clientId } : undefined;
    return this.http.get<Role[]>(`${this.platform}/roles`, { params });
  }

  createRole(request: CreateRoleRequest): Observable<Role> {
    return this.http.post<Role>(`${this.platform}/roles`, request);
  }

  updateRole(id: string, request: UpdateRoleRequest): Observable<Role> {
    return this.http.put<Role>(`${this.platform}/roles/${id}`, request);
  }

  deleteRole(id: string): Observable<void> {
    return this.http.delete<void>(`${this.platform}/roles/${id}`);
  }

  setRolePermissions(id: string, permissionIds: string[]): Observable<Role> {
    return this.http.put<Role>(`${this.platform}/roles/${id}/permissions`, { permissionIds });
  }

  // ── Menu catalog ──
  getMenuItems(): Observable<MenuItem[]> {
    return this.http.get<MenuItem[]>(`${this.platform}/menu-items`);
  }

  createMenuItem(request: CreateMenuItemRequest): Observable<MenuItem> {
    return this.http.post<MenuItem>(`${this.platform}/menu-items`, request);
  }

  updateMenuItem(id: string, request: UpdateMenuItemRequest): Observable<MenuItem> {
    return this.http.put<MenuItem>(`${this.platform}/menu-items/${id}`, request);
  }

  deleteMenuItem(id: string): Observable<void> {
    return this.http.delete<void>(`${this.platform}/menu-items/${id}`);
  }

  // ── Users ──
  getUsers(): Observable<AccessUser[]> {
    return this.http.get<AccessUser[]>(`${this.platform}/users`);
  }

  setUserTenants(id: string, clientIds: string[], defaultClientId: string | null): Observable<AccessUser> {
    return this.http.put<AccessUser>(`${this.platform}/users/${id}/tenants`, { clientIds, defaultClientId });
  }

  setUserRoles(id: string, clientId: string, roleIds: string[]): Observable<AccessUser> {
    return this.http.put<AccessUser>(`${this.platform}/users/${id}/roles`, { clientId, roleIds });
  }

  setUserSites(id: string, clientId: string, hasAllSiteAccess: boolean, clientSiteIds: string[]): Observable<AccessUser> {
    return this.http.put<AccessUser>(`${this.platform}/users/${id}/sites`, {
      clientId, hasAllSiteAccess, clientSiteIds
    });
  }

  getSitesForTenant(tenantKey: string): Observable<AccessSite[]> {
    return this.http.get<AccessSite[]>(`${this.base}/api/core/client-sites`, {
      headers: new HttpHeaders({ 'X-Tenant-Key': tenantKey })
    });
  }
}
